// Instant service switching.
//
// The screens move between the four services with no page load. These are real
// product pages with their own URLs, so the navigation stays real -- but the
// content is fetched ahead of the click and swapped in, and history is updated,
// so the reader never sees a load.
//
// Three stages:
//   1. Prefetch every sibling on idle, so the common case is already in hand.
//   2. Prefetch again on hover for anything missed.
//   3. On click, swap <main>, update the URL, and re-run the section scripts.
//
// Anything unexpected -- a failed fetch, a missing <main>, a modified click --
// falls through to an ordinary navigation, so the links always work.
(() => {
  const MAIN = 'main';
  const cache = new Map();

  const links = () => [...document.querySelectorAll('.service-hero__rail-link, .service-hero__next')];

  const fetchPage = (url) => {
    if (cache.has(url)) return cache.get(url);
    const p = fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .catch((e) => {
        cache.delete(url);
        throw e;
      });
    cache.set(url, p);
    return p;
  };

  const warm = () => links().forEach((a) => fetchPage(a.href).catch(() => {}));

  const swap = (html, url) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector(MAIN);
    const current = document.querySelector(MAIN);
    if (!next || !current) return false;

    current.replaceWith(next);
    document.title = doc.title;

    // Scripts inserted by innerHTML never execute, so re-add the ones the new
    // sections brought with them.
    next.querySelectorAll('script').forEach((old) => {
      const s = document.createElement('script');
      [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
      s.textContent = old.textContent;
      old.replaceWith(s);
    });

    window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
    bind();
    warm();

    // Anything outside <main> that watches what is inside it -- the contact dock
    // and its IntersectionObserver -- has just lost every node it was tracking.
    document.dispatchEvent(new CustomEvent('fabrik:navigated', { detail: { url } }));
    return true;
  };

  const onClick = (event) => {
    const a = event.currentTarget;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (a.target && a.target !== '_self') return;
    if (new URL(a.href).origin !== location.origin) return;

    event.preventDefault();
    fetchPage(a.href)
      .then((html) => {
        if (swap(html, a.href)) history.pushState({ fabrikSwap: true }, '', a.href);
        else location.href = a.href;
      })
      .catch(() => {
        location.href = a.href;
      });
  };

  const bind = () => {
    links().forEach((a) => {
      if (a.dataset.fabrikSwapBound) return;
      a.dataset.fabrikSwapBound = 'true';
      a.addEventListener('mouseenter', () => fetchPage(a.href).catch(() => {}), { passive: true });
      a.addEventListener('focus', () => fetchPage(a.href).catch(() => {}), { passive: true });
      a.addEventListener('click', onClick);
    });
  };

  window.addEventListener('popstate', (e) => {
    if (!e.state || !e.state.fabrikSwap) return;
    fetchPage(location.href)
      .then((html) => swap(html, location.href) || location.reload())
      .catch(() => location.reload());
  });

  bind();
  if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2500 });
  else setTimeout(warm, 1200);
})();
